import React, { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Loader2, X } from "lucide-react";
import { fmtMoney, fmtPercent, benefitLabel } from "./detail/benefitDetailFormat";
import { WB_MEDICAL_BENEFIT_FIELDS } from "@shared/constants/wbBenefitFields";
import type { RawBenefitLine } from "./detail/blocks/WbBenefitsBlock";

export interface ComparablePlan {
  id: number;
  name: string;
  type?: string | null;
  networkType?: string | null;
  metalTier?: string | null;
  description?: string | null;
  carrierName?: string | null;
}

interface PlanComparisonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plans: ComparablePlan[];
  // Monthly premium for `planId` under the employee's current selection. Returns null when unknown.
  getPremium: (planId: number) => number | null;
  // Optional: remove a plan from the comparison without closing the dialog.
  onRemovePlan?: (planId: number) => void;
}

type PlanBenefits = Record<string, {
  description?: string;
  inNetworkDeductible?: number;
  outNetworkDeductible?: number;
  inNetworkCoinsurance?: number;
  outNetworkCoinsurance?: number;
  inNetworkCopay?: number;
  outNetworkCopay?: number;
  maxOutOfPocket?: number;
  annualMaximum?: number;
}>;

export function PlanComparisonDialog({
  open,
  onOpenChange,
  plans,
  getPremium,
  onRemovePlan,
}: PlanComparisonDialogProps) {
  // Per-plan benefits fetch. Disabled when dialog is closed to avoid background work.
  const benefitsQueries = useQueries({
    queries: plans.map((p) => ({
      queryKey: ["plan-benefits", p.id],
      enabled: open,
      staleTime: 5 * 60 * 1000,
      queryFn: async (): Promise<PlanBenefits> => {
        const res = await fetch(`/api/plan-benefits/${p.id}`, { credentials: "include" });
        if (!res.ok) return {};
        return (await res.json()) as PlanBenefits;
      },
    })),
  });

  const benefitsByPlanId = useMemo(() => {
    const m = new Map<number, PlanBenefits>();
    plans.forEach((p, i) => {
      m.set(p.id, benefitsQueries[i]?.data ?? {});
    });
    return m;
  }, [plans, benefitsQueries]);

  // The plans' raw W&B lines (Plan Builder's editable source) — when any plan
  // has them, the comparison renders those rows so it matches the library.
  // Cache key shared with useBenefitDetailData.
  const rawQueries = useQueries({
    queries: plans.map((p) => ({
      queryKey: ["plan-raw-benefits", p.id],
      enabled: open,
      staleTime: 5 * 60 * 1000,
      queryFn: async (): Promise<RawBenefitLine[]> => {
        const res = await fetch(`/api/plans/${p.id}`, { credentials: "include" });
        if (!res.ok) return [];
        const plan = await res.json();
        return Array.isArray(plan?.rawBenefits) ? plan.rawBenefits : [];
      },
    })),
  });
  const rawByPlanId = useMemo(() => {
    const m = new Map<number, RawBenefitLine[]>();
    plans.forEach((p, i) => m.set(p.id, rawQueries[i]?.data ?? []));
    return m;
  }, [plans, rawQueries]);
  const anyRaw = Array.from(rawByPlanId.values()).some((lines) =>
    lines.some((l) => l?.name && String(l.value ?? "").trim()),
  );

  const isLoading = benefitsQueries.some((q) => q.isLoading) || rawQueries.some((q) => q.isLoading);

  // Union of benefit types across selected plans (e.g., medical, prescription).
  const benefitKeys = useMemo(() => {
    const seen = new Set<string>();
    benefitsByPlanId.forEach((bens) => Object.keys(bens || {}).forEach((k) => seen.add(k)));
    return Array.from(seen);
  }, [benefitsByPlanId]);

  // Header row + plan metadata rows + premium rows + per-benefit rows.
  const renderCell = (planId: number, render: (b: PlanBenefits) => React.ReactNode) =>
    render(benefitsByPlanId.get(planId) || {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Compare Plans</DialogTitle>
          <DialogDescription>
            Side-by-side comparison of the {plans.length} selected {plans.length === 1 ? "plan" : "plans"}.
          </DialogDescription>
        </DialogHeader>

        {plans.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">No plans selected.</div>
        ) : (
          <ScrollArea className="flex-1 overflow-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-background z-10">
                <tr>
                  <th className="text-left p-3 w-44 border-b font-medium text-muted-foreground">Field</th>
                  {plans.map((p) => (
                    <th key={p.id} className="text-left p-3 border-b align-top min-w-[200px]">
                      <div className="flex items-start justify-between gap-2">
                        <div className="space-y-1">
                          <div className="font-semibold leading-tight">{p.name}</div>
                          {p.carrierName && (
                            <div className="text-xs text-muted-foreground">{p.carrierName}</div>
                          )}
                          <div className="flex gap-1 flex-wrap">
                            {p.metalTier && <Badge variant="secondary" className="text-[10px]">{p.metalTier}</Badge>}
                            {p.networkType && <Badge variant="outline" className="text-[10px]">{p.networkType}</Badge>}
                          </div>
                        </div>
                        {onRemovePlan && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 shrink-0"
                            onClick={() => onRemovePlan(p.id)}
                            title="Remove from comparison"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <SectionRow label="Plan" />
                <Row label="Type" plans={plans} render={(p) => <span className="capitalize">{p.type || "—"}</span>} />
                <Row label="Network" plans={plans} render={(p) => p.networkType || "—"} />
                <Row label="Metal Tier" plans={plans} render={(p) => p.metalTier || "—"} />
                <Row
                  label="Description"
                  plans={plans}
                  render={(p) => (
                    <span className="text-xs text-muted-foreground">{p.description || "—"}</span>
                  )}
                />

                <SectionRow label="Cost (per month)" />
                <Row
                  label="Premium"
                  plans={plans}
                  render={(p) => {
                    const prem = getPremium(p.id);
                    return (
                      <span className="font-semibold text-green-700 dark:text-green-400">
                        {prem != null ? fmtMoney(prem) : "—"}
                      </span>
                    );
                  }}
                />
                <Row
                  label="Annual"
                  plans={plans}
                  render={(p) => {
                    const prem = getPremium(p.id);
                    return prem != null ? fmtMoney(prem * 12) : "—";
                  }}
                />

                {isLoading && benefitKeys.length === 0 && !anyRaw ? (
                  <tr>
                    <td colSpan={plans.length + 1} className="p-6 text-center">
                      <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                      Loading benefit details…
                    </td>
                  </tr>
                ) : anyRaw ? (
                  <RawBenefitRows plans={plans} rawByPlanId={rawByPlanId} />
                ) : (
                  benefitKeys.map((bk) => (
                    <BenefitGroup
                      key={bk}
                      benefitKey={bk}
                      plans={plans}
                      renderCell={renderCell}
                    />
                  ))
                )}
              </tbody>
            </table>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SectionRow({ label }: { label: string }) {
  return (
    <tr>
      <td colSpan={99} className="bg-muted/40 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b">
        {label}
      </td>
    </tr>
  );
}

function Row<T extends { id: number }>({
  label,
  plans,
  render,
}: {
  label: string;
  plans: T[];
  render: (p: T) => React.ReactNode;
}) {
  return (
    <tr className="border-b">
      <td className="p-3 align-top text-muted-foreground font-medium">{label}</td>
      {plans.map((p) => (
        <td key={p.id} className="p-3 align-top">{render(p)}</td>
      ))}
    </tr>
  );
}

// Rows driven by the SAME field set/grouping as the Plan Builder's benefits
// editor, one column per plan, values straight from each plan's raw W&B lines.
// A trailing "Other benefits" group carries any non-canonical lines.
function RawBenefitRows({
  plans,
  rawByPlanId,
}: {
  plans: ComparablePlan[];
  rawByPlanId: Map<number, RawBenefitLine[]>;
}) {
  const valueFor = (planId: number, name: string): string => {
    const lines = rawByPlanId.get(planId) || [];
    const pick = (network: "in" | "out") =>
      lines.find((l) => l.name === name && (l.network ?? "in") === network)?.value?.trim() ?? "";
    const inValue = pick("in");
    const outValue = pick("out");
    if (!inValue && !outValue) return "—";
    if (!outValue) return inValue;
    if (!inValue) return `OON: ${outValue}`;
    return `${inValue} · OON: ${outValue}`;
  };
  const anyValue = (name: string) => plans.some((p) => valueFor(p.id, name) !== "—");

  const canonicalNames = new Set(
    WB_MEDICAL_BENEFIT_FIELDS.flatMap((g) => g.fields.map((f) => f.name)),
  );
  const otherNames = Array.from(
    new Set(
      plans.flatMap((p) =>
        (rawByPlanId.get(p.id) || [])
          .filter((l) => l?.name && String(l.value ?? "").trim() && !canonicalNames.has(l.name))
          .map((l) => l.name),
      ),
    ),
  );

  return (
    <>
      {WB_MEDICAL_BENEFIT_FIELDS.map((group) => {
        const fields = group.fields.filter((f) => anyValue(f.name));
        if (fields.length === 0) return null;
        return (
          <React.Fragment key={group.group}>
            <SectionRow label={group.group} />
            {fields.map((f) => (
              <tr key={f.name} className="border-b">
                <td className="p-3 align-top text-muted-foreground font-medium">{f.label}</td>
                {plans.map((p) => (
                  <td key={p.id} className="p-3 align-top">{valueFor(p.id, f.name)}</td>
                ))}
              </tr>
            ))}
          </React.Fragment>
        );
      })}
      {otherNames.length > 0 && (
        <>
          <SectionRow label="Other benefits" />
          {otherNames.map((name) => (
            <tr key={name} className="border-b">
              <td className="p-3 align-top text-muted-foreground font-medium">{name}</td>
              {plans.map((p) => (
                <td key={p.id} className="p-3 align-top">{valueFor(p.id, name)}</td>
              ))}
            </tr>
          ))}
        </>
      )}
    </>
  );
}

function BenefitGroup({
  benefitKey,
  plans,
  renderCell,
}: {
  benefitKey: string;
  plans: ComparablePlan[];
  renderCell: (planId: number, render: (b: PlanBenefits) => React.ReactNode) => React.ReactNode;
}) {
  return (
    <>
      <SectionRow label={benefitLabel(benefitKey)} />
      <tr className="border-b">
        <td className="p-3 align-top text-muted-foreground font-medium">In-Network Deductible</td>
        {plans.map((p) => (
          <td key={p.id} className="p-3 align-top">
            {renderCell(p.id, (b) => fmtMoney(b[benefitKey]?.inNetworkDeductible))}
          </td>
        ))}
      </tr>
      <tr className="border-b">
        <td className="p-3 align-top text-muted-foreground font-medium">In-Network Copay</td>
        {plans.map((p) => (
          <td key={p.id} className="p-3 align-top">
            {renderCell(p.id, (b) => fmtMoney(b[benefitKey]?.inNetworkCopay))}
          </td>
        ))}
      </tr>
      <tr className="border-b">
        <td className="p-3 align-top text-muted-foreground font-medium">In-Network Coinsurance</td>
        {plans.map((p) => (
          <td key={p.id} className="p-3 align-top">
            {renderCell(p.id, (b) => fmtPercent(b[benefitKey]?.inNetworkCoinsurance))}
          </td>
        ))}
      </tr>
      <tr className="border-b">
        <td className="p-3 align-top text-muted-foreground font-medium">Max Out-of-Pocket</td>
        {plans.map((p) => (
          <td key={p.id} className="p-3 align-top">
            {renderCell(p.id, (b) => fmtMoney(b[benefitKey]?.maxOutOfPocket))}
          </td>
        ))}
      </tr>
      <tr className="border-b">
        <td className="p-3 align-top text-muted-foreground font-medium">Out-of-Network Deductible</td>
        {plans.map((p) => (
          <td key={p.id} className="p-3 align-top">
            {renderCell(p.id, (b) => fmtMoney(b[benefitKey]?.outNetworkDeductible))}
          </td>
        ))}
      </tr>
      <tr className="border-b">
        <td className="p-3 align-top text-muted-foreground font-medium">Out-of-Network Copay</td>
        {plans.map((p) => (
          <td key={p.id} className="p-3 align-top">
            {renderCell(p.id, (b) => fmtMoney(b[benefitKey]?.outNetworkCopay))}
          </td>
        ))}
      </tr>
      <tr className="border-b">
        <td className="p-3 align-top text-muted-foreground font-medium">Annual Maximum</td>
        {plans.map((p) => (
          <td key={p.id} className="p-3 align-top">
            {renderCell(p.id, (b) => fmtMoney(b[benefitKey]?.annualMaximum))}
          </td>
        ))}
      </tr>
      <tr className="border-b">
        <td className="p-3 align-top text-muted-foreground font-medium">Notes</td>
        {plans.map((p) => (
          <td key={p.id} className="p-3 align-top">
            {renderCell(p.id, (b) => (
              <span className="text-xs text-muted-foreground">{b[benefitKey]?.description || "—"}</span>
            ))}
          </td>
        ))}
      </tr>
    </>
  );
}
